import { useCreditBalance, useSubscribe, useTopup, useBillingPortal } from "../hooks/useCredits";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { TOPUP_PACKS, SUBSCRIPTION } from "../../../shared/constants";

export function Billing() {
  const { data: balanceData, isLoading } = useCreditBalance();
  const subscribeMutation = useSubscribe();
  const topupMutation = useTopup();
  const portalMutation = useBillingPortal();

  const balance = balanceData?.data;

  function handleSubscribe() {
    subscribeMutation.mutate(
      {
        success_url: `${window.location.origin}/billing?success=subscription`,
        cancel_url: `${window.location.origin}/billing`,
      },
      {
        onSuccess: (res) => {
          window.location.href = res.data.checkout_url;
        },
      },
    );
  }

  function handleTopup(packId: string) {
    topupMutation.mutate(
      {
        pack: packId as "10" | "30" | "100",
        success_url: `${window.location.origin}/billing?success=topup`,
        cancel_url: `${window.location.origin}/billing`,
      },
      {
        onSuccess: (res) => {
          window.location.href = res.data.checkout_url;
        },
      },
    );
  }

  function handleManageBilling() {
    portalMutation.mutate(`${window.location.origin}/billing`, {
      onSuccess: (res) => {
        window.location.href = res.data.portal_url;
      },
    });
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Credit Balance */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Credits</h2>
            <p className="mt-1 text-3xl font-bold text-slate-900">
              {balance?.credits ?? 0}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Each content run uses 1 credit
            </p>
          </div>
          <Badge
            variant={
              balance?.subscription_status === "active"
                ? "success"
                : balance?.low_balance
                  ? "error"
                  : "default"
            }
          >
            {balance?.subscription_status === "active"
              ? "Pro"
              : balance?.subscription_status === "past_due"
                ? "Past Due"
                : "Free"}
          </Badge>
        </div>
        {balance?.low_balance && (
          <div className="mt-4 rounded-lg bg-amber-50 p-3">
            <p className="text-sm text-amber-700">
              Low credit balance. Top up or subscribe to keep creating content.
            </p>
          </div>
        )}
      </section>

      {/* Subscription */}
      {balance?.subscription_status !== "active" && (
        <section className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="mb-2 text-lg font-semibold text-slate-900">
            HelloConvo Pro
          </h2>
          <p className="mb-4 text-sm text-slate-500">
            ${SUBSCRIPTION.base_price_cents / 100}/month &mdash;{" "}
            {SUBSCRIPTION.included_credits} content runs included each month.
          </p>
          <Button
            onClick={handleSubscribe}
            disabled={subscribeMutation.isPending}
          >
            {subscribeMutation.isPending ? "Redirecting..." : "Subscribe"}
          </Button>
        </section>
      )}

      {/* Manage Subscription */}
      {balance?.subscription_status === "active" && (
        <section className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="mb-2 text-lg font-semibold text-slate-900">
            Manage Subscription
          </h2>
          <p className="mb-4 text-sm text-slate-500">
            Update payment method, view invoices, or cancel your subscription.
          </p>
          <Button
            variant="secondary"
            onClick={handleManageBilling}
            disabled={portalMutation.isPending}
          >
            {portalMutation.isPending ? "Opening..." : "Manage billing"}
          </Button>
        </section>
      )}

      {/* Top-up Packs */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-slate-900">
          Top up credits
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {TOPUP_PACKS.map((pack) => (
            <button
              key={pack.id}
              onClick={() => handleTopup(pack.id)}
              disabled={topupMutation.isPending}
              className="rounded-xl border border-slate-200 bg-white p-5 text-left transition-all hover:border-blue-300 hover:shadow-sm disabled:opacity-50"
            >
              <p className="text-2xl font-bold text-slate-900">
                {pack.credits} credits
              </p>
              <p className="mt-1 text-sm text-slate-500">{pack.label}</p>
              <p className="mt-3 text-lg font-semibold text-blue-600">
                ${(pack.price_cents / 100).toFixed(2)}
              </p>
              <p className="text-xs text-slate-400">
                ${(pack.price_cents / pack.credits / 100).toFixed(2)}/credit
              </p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
